import { createFileRoute, Link, redirect } from '@tanstack/react-router'
import { ArrowLeft, Check, LoaderCircle, LogOut, Trash2 } from 'lucide-react'
import { motion } from 'motion/react'
import { useState } from 'react'

import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Label } from '@/components/ui/label'
import { Separator } from '@/components/ui/separator'
import { Switch } from '@/components/ui/switch'
import {
  useLogoutMutation,
  useUpdateProfileMutation,
} from '@/features/auth/api'
import { SubscriptionPanel } from '@/features/subscriptions/components/subscription-panel'
import { cn } from '@/lib/utils'
import { useAuthStore } from '@/stores/auth-store'
import { useBackgroundStore } from '@/stores/background-store'

export const Route = createFileRoute('/settings')({
  beforeLoad: ({ context }) => {
    if (!context.auth.isAuthenticated) {
      throw redirect({ to: '/login' })
    }
  },
  component: SettingsPage,
})

/* ─── Types ─────────────────────────────────── */

type BackgroundStyle = 'dots' | 'grid' | 'clean' | 'gradient' | 'subtle'

interface BackgroundOption {
  value: BackgroundStyle
  label: string
  previewClass: string
}

/* ─── Data ──────────────────────────────────── */

const BACKGROUND_OPTIONS: BackgroundOption[] = [
  {
    value: 'dots',
    label: 'Dots',
    previewClass:
      'bg-[radial-gradient(circle,oklch(0.82_0_0)_1px,transparent_1px)] bg-[length:8px_8px]',
  },
  {
    value: 'grid',
    label: 'Grid',
    previewClass:
      'bg-[linear-gradient(oklch(0.9_0_0)_1px,transparent_1px),linear-gradient(90deg,oklch(0.9_0_0)_1px,transparent_1px)] bg-[length:12px_12px]',
  },
  {
    value: 'clean',
    label: 'Clean',
    previewClass: 'bg-background',
  },
  {
    value: 'gradient',
    label: 'Gradient',
    previewClass:
      'bg-[linear-gradient(135deg,oklch(0.97_0.01_280),oklch(0.95_0.01_200))]',
  },
  {
    value: 'subtle',
    label: 'Subtle',
    previewClass:
      'bg-[radial-gradient(circle_at_50%_50%,oklch(0.96_0.005_280),oklch(0.99_0_0))]',
  },
]

/* ─── Animation variants ────────────────────── */

const stagger = {
  hidden: {},
  visible: { transition: { staggerChildren: 0.06 } },
}

const fadeUp = {
  hidden: { opacity: 0, y: 20, scale: 0.97 },
  visible: {
    opacity: 1,
    y: 0,
    scale: 1,
    transition: { duration: 0.35, ease: [0.22, 1, 0.36, 1] as const },
  },
}

/* ─── Components ────────────────────────────── */

interface SectionProps {
  title: string
  description?: string
  children: React.ReactNode
}

function Section({ title, description, children }: SectionProps) {
  return (
    <motion.section variants={fadeUp}>
      <div className="mb-4">
        <h2 className="text-lg font-semibold tracking-tight">{title}</h2>
        {description && (
          <p className="text-sm text-muted-foreground">{description}</p>
        )}
      </div>
      <div className="rounded-xl border bg-card p-5 shadow-card">
        {children}
      </div>
    </motion.section>
  )
}

interface BackgroundPickerProps {
  selected: BackgroundStyle
  onSelect: (bg: BackgroundStyle) => void
}

function BackgroundPicker({ selected, onSelect }: BackgroundPickerProps) {
  return (
    <div className="grid grid-cols-5 gap-3">
      {BACKGROUND_OPTIONS.map((opt) => {
        const isActive = selected === opt.value
        return (
          <button
            key={opt.value}
            type="button"
            onClick={() => onSelect(opt.value)}
            className="flex flex-col items-center gap-2"
          >
            <div
              className={cn(
                'relative size-16 rounded-lg border-2 transition-all sm:size-20',
                opt.previewClass,
                isActive
                  ? 'border-primary ring-2 ring-primary/20'
                  : 'border-border hover:border-foreground/30',
              )}
            >
              {isActive && (
                <div className="absolute inset-0 flex items-center justify-center">
                  <div className="flex size-5 items-center justify-center rounded-full bg-primary">
                    <Check className="size-3 text-primary-foreground" />
                  </div>
                </div>
              )}
            </div>
            <span
              className={cn(
                'text-xs',
                isActive
                  ? 'font-medium text-foreground'
                  : 'text-muted-foreground',
              )}
            >
              {opt.label}
            </span>
          </button>
        )
      })}
    </div>
  )
}

/* ─── Page ──────────────────────────────────── */

function SettingsPage() {
  const background = useBackgroundStore((s) => s.background)
  const setBackground = useBackgroundStore((s) => s.setBackground)
  const user = useAuthStore((s) => s.user)
  const [displayName, setDisplayName] = useState(user?.display_name ?? '')
  const [savedFlash, setSavedFlash] = useState(false)
  const updateProfile = useUpdateProfileMutation()
  const logout = useLogoutMutation()

  const profileDirty = displayName.trim() !== (user?.display_name ?? '')
  const profileSubmittable = displayName.trim().length > 0 && profileDirty

  function onSaveProfile() {
    if (!profileSubmittable) return
    updateProfile.mutate(
      { display_name: displayName.trim() },
      {
        onSuccess: () => {
          setSavedFlash(true)
          setTimeout(() => setSavedFlash(false), 2000)
        },
      },
    )
  }

  return (
    <div className="relative min-h-screen px-4 pb-16 pt-6 sm:px-6 lg:px-8">
      {/* Header */}
      <motion.header
        className="mx-auto mb-8 flex max-w-2xl items-center gap-3"
        initial={{ opacity: 0, y: -12 }}
        animate={{ opacity: 1, y: 0 }}
        transition={{ duration: 0.4, ease: [0.22, 1, 0.36, 1] }}
      >
        <Button variant="ghost" size="icon" className="shrink-0" asChild>
          <Link to="/dashboard" aria-label="Back to dashboard">
            <ArrowLeft className="size-5" />
          </Link>
        </Button>
        <h1 className="text-2xl font-bold tracking-tight">Settings</h1>
      </motion.header>

      <motion.div
        className="mx-auto flex max-w-2xl flex-col gap-8"
        variants={stagger}
        initial="hidden"
        animate="visible"
      >
        {/* Profile */}
        <Section title="Profile">
          <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="full-name">Full name</Label>
              <Input
                id="full-name"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                placeholder="Your name"
                className="max-w-sm"
                disabled={updateProfile.isPending}
              />
            </div>
            <div className="flex flex-col gap-1.5">
              <Label htmlFor="email">Email</Label>
              <Input
                id="email"
                type="email"
                value={user?.email ?? ''}
                disabled
                readOnly
                className="max-w-sm"
              />
              <p className="text-xs text-muted-foreground">
                Contact support to change your email.
              </p>
            </div>
            <div className="flex items-center gap-3 pt-1">
              <Button
                size="sm"
                onClick={onSaveProfile}
                disabled={!profileSubmittable || updateProfile.isPending}
              >
                {updateProfile.isPending && (
                  <LoaderCircle className="size-3.5 animate-spin" />
                )}
                Save changes
              </Button>
              {savedFlash && (
                <span className="flex items-center gap-1 text-xs text-success">
                  <Check className="size-3.5" />
                  Saved
                </span>
              )}
              {updateProfile.isError && (
                <span className="text-xs text-destructive">
                  Couldn't save. Try again.
                </span>
              )}
            </div>
          </div>
        </Section>

        {/* Subscription */}
        <Section
          title="Subscription"
          description="Manage your plan and billing."
        >
          <SubscriptionPanel />
        </Section>

        {/* Appearance */}
        <Section
          title="Appearance"
          description="Choose a background for your workspace."
        >
          <BackgroundPicker selected={background} onSelect={setBackground} />
        </Section>

        {/* Notifications */}
        <Section
          title="Notifications"
          description="Coming soon — preferences aren't wired up yet."
        >
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Render complete</p>
                <p className="text-xs text-muted-foreground">
                  Get notified when your video is ready to download.
                </p>
              </div>
              <Switch
                defaultChecked
                disabled
                aria-label="Render complete notifications"
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Product updates</p>
                <p className="text-xs text-muted-foreground">
                  New features, models, and improvements.
                </p>
              </div>
              <Switch
                defaultChecked
                disabled
                aria-label="Product update notifications"
              />
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Tips and tutorials</p>
                <p className="text-xs text-muted-foreground">
                  Learn how to get the most out of Melodex.
                </p>
              </div>
              <Switch disabled aria-label="Tips and tutorials notifications" />
            </div>
          </div>
        </Section>

        {/* Danger zone */}
        <Section title="Account">
          <div className="flex flex-col gap-4">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">Sign out</p>
                <p className="text-xs text-muted-foreground">
                  Sign out of your account on this device.
                </p>
              </div>
              <Button
                variant="outline"
                size="sm"
                onClick={() => logout.mutate()}
                disabled={logout.isPending}
              >
                {logout.isPending ? (
                  <LoaderCircle className="size-3.5 animate-spin" />
                ) : (
                  <LogOut className="size-3.5" />
                )}
                Sign out
              </Button>
            </div>
            <Separator />
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium text-destructive">
                  Delete account
                </p>
                <p className="text-xs text-muted-foreground">
                  Email{' '}
                  <a
                    href="mailto:apaterogroup@gmail.com?subject=Delete%20my%20Melodex%20account"
                    className="underline underline-offset-2"
                  >
                    apaterogroup@gmail.com
                  </a>{' '}
                  to permanently delete your account.
                </p>
              </div>
              <Button variant="destructive" size="sm" disabled>
                <Trash2 className="size-3.5" />
                Delete
              </Button>
            </div>
          </div>
        </Section>
      </motion.div>
    </div>
  )
}
